import {
  Alert,
  Anchor,
  Button,
  Code,
  Divider,
  Group,
  NumberInput,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { datum, datumZaApi, denar, tocke as tockeZapis } from '../oblika/oblika';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { opisGibanja, VRSTE_GIBANJ } from '../narocnina/narocnina';
import { ZnackaNarocnine } from '../narocnina/ZnackaNarocnine';
import { cas, zadnjihDni } from './skrbnistvo';
import { glave, obNapaki } from './zeton';

type Narocnina = Shema['NarocninaSkrbnik'];

async function klic<T>(k: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  try {
    return await podatki(k);
  } catch (e) {
    obNapaki(e);
    throw e;
  }
}

/**
 * Naročnina instance v skrbništvu: stanje točk in plačano do, naslednji obračun (prvi obračunski dan za obstoječe
 * stranke, prazno = brez omejitve), ročno dodajanje/odvzem točk s komentarjem in zgodovina gibanj. Vse izračuna
 * strežnik (odbitje se zgodi takoj, ko je točk dovolj).
 */
export function NarocninaInstance({ companyId }: { companyId: number }) {
  const kljuc = ['skrbnik', 'narocnina', companyId];
  const narocnina = useQuery({
    queryKey: kljuc,
    queryFn: ({ signal }) =>
      klic(
        api.GET('/api/skrbnik/instance/{companyId}/narocnina', {
          signal,
          headers: glave(),
          params: { path: { companyId } },
        }),
      ),
  });
  if (!narocnina.data) {
    return (
      <Kartica naslov="Naročnina in točke">
        <StanjeNalaganja
          napaka={narocnina.error}
          ponovi={() => void narocnina.refetch()}
          naslov="Naročnine ni bilo mogoče naložiti"
        />
      </Kartica>
    );
  }
  return <Vsebina key={narocnina.data.naslednjiObracun ?? ''} n={narocnina.data} companyId={companyId} kljuc={kljuc} />;
}

function Vsebina({ n, companyId, kljuc }: { n: Narocnina; companyId: number; kljuc: unknown[] }) {
  const odjemalec = useQueryClient();
  const [obracun, setObracun] = useState<string | null>(n.naslednjiObracun ?? null);
  const [tocke, setTocke] = useState<number | string>('');
  const [komentar, setKomentar] = useState('');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<'obracun' | 'tocke' | null>(null);

  const izvedi = async (kaj: 'obracun' | 'tocke', k: () => Promise<Narocnina>, sporocilo_: string) => {
    setDela(kaj);
    setNapake({});
    setNapaka(null);
    try {
      odjemalec.setQueryData(kljuc, await k());
      notifications.show({ color: 'green', message: sporocilo_ });
      if (kaj === 'tocke') {
        setTocke('');
        setKomentar('');
      }
      await odjemalec.invalidateQueries({ queryKey: ['skrbnik', 'instance'] });
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(null);
    }
  };

  const shraniObracun = () =>
    izvedi(
      'obracun',
      () =>
        klic(
          api.PUT('/api/skrbnik/instance/{companyId}/narocnina/obracun', {
            headers: glave(),
            params: { path: { companyId } },
            body: { naslednjiObracun: datumZaApi(obracun) },
          }),
        ),
      obracun ? 'Obračun je nastavljen.' : 'Instanca je brez omejitve.',
    );

  const dodajTocke = () =>
    izvedi(
      'tocke',
      () =>
        klic(
          api.POST('/api/skrbnik/instance/{companyId}/narocnina/tocke', {
            headers: glave(),
            params: { path: { companyId } },
            body: { tocke: tocke === '' ? undefined : Number(tocke), komentar },
          }),
        ),
      Number(tocke) < 0 ? 'Točke so odvzete.' : 'Točke so dodane.',
    );

  return (
    <Kartica naslov="Naročnina in točke" dejanja={<ZnackaNarocnine stanje={n.stanje} />}>
      <Stack gap="md">
        <Podatki
          vrstice={[
            [
              'Stanje točk',
              `${tockeZapis(n.tocke)} točk${n.mesecevPokrije ? ` (pokrije ${n.mesecevPokrije} mes.)` : ''}`,
            ],
            ['Mesečna naročnina', `${denar(n.narocnina)} točk`],
            ['Plačano do', n.placanoDo ? datum(n.placanoDo) : 'brez omejitve'],
            ['Naslednji obračun', n.naslednjiObracun ? datum(n.naslednjiObracun) : ''],
          ]}
        />
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <Stack gap="xs">
            <DatePickerInput
              label="Naslednji obračun"
              description="Ta dan se odšteje mesečna naročnina (prvi obračunski dan za obstoječe stranke). Prazno = brez omejitve."
              placeholder="Brez omejitve"
              value={obracun}
              onChange={setObracun}
              valueFormat="D. M. YYYY"
              clearable
              error={napake.naslednjiObracun}
            />
            <Group>
              <Button
                variant="default"
                onClick={() => void shraniObracun()}
                loading={dela === 'obracun'}
                disabled={(obracun ?? null) === (n.naslednjiObracun ?? null) || dela !== null}
              >
                Shrani obračun
              </Button>
            </Group>
          </Stack>
          <Stack gap="xs">
            <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="xs">
              <NumberInput
                size="md"
                label="Točke"
                description="Z minusom za odvzem"
                value={tocke}
                onChange={setTocke}
                decimalScale={2}
                decimalSeparator=","
                thousandSeparator="."
                hideControls
                error={napake.tocke}
              />
              <TextInput
                label="Komentar"
                description="Vidi se v zgodovini"
                value={komentar}
                onChange={(e) => setKomentar(e.currentTarget.value)}
                error={napake.komentar}
              />
            </SimpleGrid>
            <Group>
              <Button onClick={() => void dodajTocke()} loading={dela === 'tocke'} disabled={dela !== null}>
                {Number(tocke) < 0 ? 'Odvzemi točke' : 'Dodaj točke'}
              </Button>
            </Group>
          </Stack>
        </SimpleGrid>
        <NakaziloTrr n={n} companyId={companyId} kljuc={kljuc} />
        <Nakazilo n={n} companyId={companyId} kljuc={kljuc} />
        {napaka && (
          <Alert color="red" variant="light" role="alert">
            {napaka}
          </Alert>
        )}
        {(n.gibanja ?? []).length === 0 ? (
          <Text size="sm" c="dimmed">
            Ni gibanj točk.
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={900}>
            <Table verticalSpacing={6}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Čas</Table.Th>
                  <Table.Th>Vrsta</Table.Th>
                  <Table.Th ta="right">Točke</Table.Th>
                  <Table.Th ta="right">Prej</Table.Th>
                  <Table.Th ta="right">Potem</Table.Th>
                  <Table.Th>Obdobje</Table.Th>
                  <Table.Th>Komentar</Table.Th>
                  <Table.Th>Kdo</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(n.gibanja ?? []).map((g) => (
                  <Table.Tr key={g.id}>
                    <Table.Td className="brez-preloma">{cas(g.cas)}</Table.Td>
                    <Table.Td>{VRSTE_GIBANJ[g.vrsta ?? ''] ?? g.vrsta}</Table.Td>
                    <Table.Td ta="right" className="brez-preloma">
                      <Text size="sm" fw={600} c={(g.tocke ?? 0) < 0 ? 'red' : 'green'}>
                        {(g.tocke ?? 0) > 0 ? '+' : ''}
                        {tockeZapis(g.tocke)}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right" className="brez-preloma" c="dimmed">
                      {tockeZapis(g.stanjePrej)}
                    </Table.Td>
                    <Table.Td ta="right" className="brez-preloma" fw={500}>
                      {tockeZapis(g.stanjePo)}
                    </Table.Td>
                    <Table.Td className="brez-preloma">
                      {g.obdobjeOd ? `${datum(g.obdobjeOd)} – ${datum(g.obdobjeDo)}` : ''}
                    </Table.Td>
                    <Table.Td>{opisGibanja(g.znesek, g.komentar)}</Table.Td>
                    <Table.Td>{g.uporabnik}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Kartica>
  );
}

/**
 * Prejeto nakazilo na naš TRR (z izpiska, sklic instance): znesek, datum plačila in komentar. Točke + bonus brez
 * stroška; odbitje glede na datum plačila (pravočasno plačilo, vneseno kasneje, ne plača ponovnega vklopa).
 */
function NakaziloTrr({ n, companyId, kljuc }: { n: Narocnina; companyId: number; kljuc: unknown[] }) {
  const odjemalec = useQueryClient();
  const [znesek, setZnesek] = useState<number | string>('');
  const [datumPlacila, setDatumPlacila] = useState<string | null>(() => zadnjihDni(1)[0] ?? null);
  const [komentar, setKomentar] = useState('');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);

  const vnesi = async () => {
    setDela(true);
    setNapake({});
    setNapaka(null);
    try {
      odjemalec.setQueryData(
        kljuc,
        await klic(
          api.POST('/api/skrbnik/instance/{companyId}/nakazilo/trr', {
            headers: glave(),
            params: { path: { companyId } },
            body: { znesek: znesek === '' ? undefined : Number(znesek), datum: datumZaApi(datumPlacila), komentar },
          }),
        ),
      );
      notifications.show({ color: 'green', message: 'Nakazilo je vneseno.' });
      setZnesek('');
      setKomentar('');
      await odjemalec.invalidateQueries({ queryKey: ['skrbnik', 'instance'] });
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  return (
    <Stack gap="xs">
      <Divider label="Nakazilo na naš TRR" labelPosition="left" />
      <Text size="sm">
        Sklic instance: <Code>{n.sklic}</Code>
      </Text>
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xs">
        <NumberInput
          size="md"
          label="Znesek (€)"
          value={znesek}
          onChange={setZnesek}
          min={0}
          decimalScale={2}
          decimalSeparator=","
          thousandSeparator="."
          hideControls
          error={napake.znesek}
        />
        <DatePickerInput
          label="Datum plačila"
          description="Z izpiska"
          value={datumPlacila}
          onChange={setDatumPlacila}
          valueFormat="D. M. YYYY"
          error={napake.datum}
        />
        <TextInput
          label="Komentar nakazila"
          description="Npr. plačnik"
          value={komentar}
          onChange={(e) => setKomentar(e.currentTarget.value)}
          error={napake.komentar}
        />
      </SimpleGrid>
      <Group>
        <Button variant="light" onClick={() => void vnesi()} loading={dela}>
          Vnesi nakazilo
        </Button>
      </Group>
      {napaka && (
        <Alert color="red" variant="light" role="alert">
          {napaka}
        </Alert>
      )}
    </Stack>
  );
}

/**
 * Podatki za nakazilo instance pri Stripu: IBAN se ustvari ob prvem odprtju naročnine pri stranki ali tu (»Pripravi
 * IBAN«). V testnem načinu še simulirano nakazilo – točke pridejo prek webhooka kot pri pravem.
 */
function Nakazilo({ n, companyId, kljuc }: { n: Narocnina; companyId: number; kljuc: unknown[] }) {
  const odjemalec = useQueryClient();
  const [znesek, setZnesek] = useState<number | string>('');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<'pripravi' | 'simuliraj' | null>(null);
  const p = n.nakazilo;

  const izvedi = async (kaj: 'pripravi' | 'simuliraj', k: () => Promise<void>) => {
    setDela(kaj);
    setNapake({});
    setNapaka(null);
    try {
      await k();
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(null);
    }
  };

  const pripravi = () =>
    izvedi('pripravi', async () => {
      odjemalec.setQueryData(
        kljuc,
        await klic(
          api.POST('/api/skrbnik/instance/{companyId}/nakazilo', { headers: glave(), params: { path: { companyId } } }),
        ),
      );
    });

  const simuliraj = () =>
    izvedi('simuliraj', async () => {
      await klic(
        api.POST('/api/skrbnik/instance/{companyId}/nakazilo/simuliraj', {
          headers: glave(),
          params: { path: { companyId } },
          body: { znesek: znesek === '' ? undefined : Number(znesek) },
        }),
      );
      notifications.show({
        color: 'green',
        message: 'Nakazilo je poslano Stripu. Točke se pripišejo, ko Stripe pošlje webhook (nekaj sekund).',
      });
      setZnesek('');
      // webhook obdela Stripe asinhrono – osvežimo malo kasneje
      setTimeout(() => void odjemalec.invalidateQueries({ queryKey: kljuc }), 4000);
    });

  return (
    <Stack gap="xs">
      <Divider label="Nakazilo prek Stripa" labelPosition="left" />
      {p ? (
        <Podatki
          vrstice={[
            ['IBAN', <Code key="iban">{p.iban}</Code>],
            ['BIC', p.bic ?? ''],
            ['Prejemnik', p.imetnik ?? ''],
            [
              'Stranka pri Stripu',
              <Anchor
                key="stranka"
                href={`https://dashboard.stripe.com/${p.nacin === 'test' ? 'test/' : ''}customers/${p.stranka}`}
                target="_blank"
                rel="noreferrer"
                size="sm"
              >
                {p.stranka}
              </Anchor>,
            ],
          ]}
        />
      ) : (
        <Group gap="sm">
          <Text size="sm" c="dimmed" style={{ flex: '1 1 260px' }}>
            Stranka pri Stripu še ni ustvarjena. Ustvari se sama, ko kdo v instanci odpre zaslon Naročnina.
          </Text>
          <Button variant="default" onClick={() => void pripravi()} loading={dela === 'pripravi'}>
            Pripravi IBAN
          </Button>
        </Group>
      )}
      {p?.nacin === 'test' && (
        <Group gap="xs" align="flex-end">
          <NumberInput
            size="md"
            label="Simuliraj nakazilo (€)"
            description="Samo testni način Stripa"
            value={znesek}
            onChange={setZnesek}
            min={0}
            decimalScale={2}
            decimalSeparator=","
            thousandSeparator="."
            hideControls
            error={napake.znesek}
            maw={240}
          />
          <Button variant="light" onClick={() => void simuliraj()} loading={dela === 'simuliraj'}>
            Simuliraj
          </Button>
        </Group>
      )}
      {napaka && (
        <Alert color="red" variant="light" role="alert">
          {napaka}
        </Alert>
      )}
    </Stack>
  );
}
